import * as React from "react";
import { observer, inject } from "mobx-react";
import * as moment from "moment";
import { Link } from "react-router-dom";
import BootstrapTable from "react-bootstrap-table-next";

import { ConfigurationStore } from "src/stores/ConfigurationStore";
import { HorseForSaleStore } from "src/stores/HorseForSaleStore";
import { HorseForSaleListStore, HorseForSaleListState } from "src/stores/HorseForSaleListStore";
import { CustomerEndpoints } from "src/endpoints/CustomerEndpoints";

import LoadingWidget from "../common/LoadingWidget";
import { CustomerLink } from "../common/CustomerLink";
import CustomerMultiselect from "../common/CustomerMultiselect";
import { SelectGroup } from "../common/SimpleFormComponents";
import { HorseLink } from "../common/HorseLink";
import { SimpleConfirmation } from "../common/SimpleConfirmAlert";
import { successMessage, errorMessage } from "../common/Toast";

import {
    SimpleHorseForSale,
    HorseForSale,
    HorseForSaleState,
    HorseSale,
    AllPostApprovedStates,
    PostApprovalWithdrawnStates
} from "src/model/HorseForSale";
import { SimpleCustomer } from "src/model/Customer";
import { SaleDate } from "src/model/HorseForSale";

import { SwapHipNumbersDialog } from "./SwapHipNumbersDialog";

type ArrayInfo = {
    date: string | null;
    length: number;
};

const DEFAULT_STATES = AllPostApprovedStates();

export interface Props {
    configurationStore?: ConfigurationStore;
    horseForSaleListStore?: HorseForSaleListStore;
    horseForSaleStore?: HorseForSaleStore;
    customerEndpoints?: CustomerEndpoints;
}

export interface State {
    // The is the selected sale that is necessary for the entire widget to work
    selectedSale: HorseSale | null;

    // This is the list of selected rows (for swapping Hip # names)
    selectedHorsesForSale: Array<HorseForSale>;

    // Whether or not the swap hip # dialog is visible
    showDialog: boolean;
}

@inject("customerEndpoints")
@inject("horseForSaleListStore")
@inject("horseForSaleStore")
@inject("configurationStore")
@observer
export class AssignDayForSaleAndGenerateHipNumbers extends React.Component<Props, State> {
    state: State = {
        showDialog: false,
        selectedHorsesForSale: [],
        selectedSale: null
    };

    private form: HTMLFormElement | null;

    onChangeSelectConsignor = (selectedConsignors: Array<SimpleCustomer>): void => {
        this.props.horseForSaleListStore!.updateHorseForSaleListFilters({
            selectedConsignors: selectedConsignors
        });
    };

    onSelectConsignor = (consignor: SimpleCustomer) => {
        this.props.horseForSaleListStore!.updateHorseForSaleListFilters({
            selectedConsignors: [consignor]
        });
    };

    handleOnSelect = (row: HorseForSale, isSelect: boolean) => {
        if (isSelect) {
            this.setState(() => ({
                selectedHorsesForSale: [...this.state.selectedHorsesForSale, row]
            }));
        } else {
            this.setState(() => ({
                selectedHorsesForSale: this.state.selectedHorsesForSale.filter(x => x.id !== row.id)
            }));
        }
    };

    clearSelectHipNumbers = () => {
        this.setState({
            selectedHorsesForSale: []
        });
    };

    handleOnSelectSaleDate = (horseForSale: SimpleHorseForSale, date: SaleDate | null) => async (event: React.ChangeEvent<HTMLInputElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedDate = date === null ? "unassigned date" : moment(date.date).format("MMM DD, YYYY");

        try {
            const goAhead = await SimpleConfirmation(`Are you sure you'd like to assign "${selectedDate}" to ${horseForSale.horse.name}?`);
            if (!goAhead) {
                return;
            }
            // TODO: Just go straight to the store (we're not observing anything here in the UI, so we don't really need it)
            await this.props.horseForSaleStore!.horseForSaleUpdateFieldsByObject(horseForSale.id, {
                saledate: date === null ? null : date.id
            });
            successMessage(`${selectedDate.charAt(0).toUpperCase() + selectedDate.slice(1)} has been assigned to ${horseForSale.horse.name}.`);
            this.props.horseForSaleListStore!.refresh(false);
        } catch (error) {
            errorMessage(error);
        }
    };

    generateHipNumbers = (sale: HorseSale | null) => async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        if (!sale) {
            console.error(`No sale specified in generateHipNumbers. This shouldn't happen.`);
            return;
        }

        try {
            const goAhead = await SimpleConfirmation(
                `Are you sure you'd like to generate the Hip #s for all the
                Horses for Sale in the ${sale.name}? Please remember that this operation is not reversible.`
            );
            if (!goAhead) {
                return;
            }
            // TODO: Just go straight to the store (we're not observing anything here in the UI, so we don't really need it)
            await this.props.horseForSaleStore!.generateHipNumbers(sale.id);
            successMessage(`Hip #s for ${sale.name} have been successfully generated`);

            this.props.horseForSaleListStore!.updateHorseForSaleListState({
                sizePerPage: 1000,
                sortField: "hipNumber",
                sortOrder: "asc"
            });
            this.props.horseForSaleListStore!.refresh();
        } catch (error) {
            errorMessage(error);
        }
    };

    tableChanged = (type: TableChangeType, event: TableChangeNewState) => {
        if (type === "sort") {
            this.props.horseForSaleListStore!.updateHorseForSaleListState({
                sortOrder: event.sortOrder,
                sortField: event.sortField as any
            });
        }
    };

    onChangeSelectSale = (e: React.ChangeEvent<HTMLSelectElement>): void => {
        const selectedSaleId = e.target.value === "" ? null : parseInt(e.target.value, 10);
        const selectedSale = selectedSaleId ? this.props.configurationStore!.findHorseSaleById(selectedSaleId) : null;

        this.setState({ selectedSale });

        this.props.horseForSaleListStore!.reset(true);
        this.props.horseForSaleListStore!.updateListState({ sizePerPage: 1000, ...this.sortOrder }, false);
        this.props.horseForSaleListStore!.updateListFilters(
            {
                selectedSales: selectedSale !== null ? [selectedSale] : [],
                selectedStates: DEFAULT_STATES,
                selectedConsignors: []
            },
            false
        );

        // Only refresh if we have an actual value
        if (selectedSale) {
            this.props.horseForSaleListStore!.refresh();
        }
    };

    componentDidMount() {
        // We avoid refreshing here to avoid unnecessary network chatter
        this.props.horseForSaleListStore!.reset();
        this.props.horseForSaleListStore!.updateListState(
            {
                sizePerPage: 1000,
                ...this.sortOrder
            },
            false
        );
        this.props.horseForSaleListStore!.updateListFilters(
            {
                selectedSales: [],
                selectedStates: DEFAULT_STATES
            },
            false
        );
    }

    render() {
        const configurationStore = this.props.configurationStore;
        if (!configurationStore) {
            return <div>No Configuration Store found.</div>;
        }

        const horseForSaleListStore = this.props.horseForSaleListStore;
        if (!horseForSaleListStore) {
            return <div>No Horse For Sale List Store found.</div>;
        }

        const customerEndpoints = this.props.customerEndpoints;
        if (!customerEndpoints) {
            return <div>No HcustomerEndpoints found.</div>;
        }

        // Include all horses for sale in the active states that the store is already filtered to
        // *except* any horse that has been withdrawn-without-penalty w/o a hip number (implying they were
        // withdrawn before the hip numbers were generated)
        const results = horseForSaleListStore.horseForSaleList.results.filter(
            hfs => !(hfs.hipNumber === null && hfs.state === HorseForSaleState.WITHDRAWN_WITHOUT_PENALTY)
        );

        const noSaleDatesArray = results.filter(sale => sale.saleDate === null);
        const horseForSaleListFilters = horseForSaleListStore.horseForSaleListFilters;

        const currentSaleDates: Array<SaleDate> = this.state.selectedSale !== null ? this.state.selectedSale.dates : [];

        const infoTableArray: Array<ArrayInfo> = currentSaleDates.map(saleDate => {
            return {
                date: saleDate.date,
                length: results.filter(horseForSale => (horseForSale.saleDate && horseForSale.saleDate.date) === saleDate.date).length
            };
        });
        infoTableArray.unshift({ date: null, length: noSaleDatesArray.length });

        // console.log("deactivateGenerationHipNumbersButton");
        // console.log("this.state.selectedSale === null", this.state.selectedSale === null);
        // console.log("horseForSaleListFilters.selectedConsignors.length > 0", horseForSaleListFilters.selectedConsignors.length > 0 );
        // console.log("results.some( sale => sale.hipNumber !== null)", results.some( sale => sale.hipNumber !== null));
        // console.log("noSaleDatesArray.length > 0", noSaleDatesArray.length > 0);
        // console.log("results.some( sale => sale.horse.dam === null)", results.some( sale => sale.horse.dam === null));
        // console.log("results.some( sale => sale.consignor === null)", results.some( sale => sale.consignor === null));

        const deactivateGenerationHipNumbersButton =
            this.state.selectedSale === null ||
            horseForSaleListFilters.selectedConsignors.length > 0 ||
            results.some(sale => sale.hipNumber !== null) ||
            noSaleDatesArray.length > 0 ||
            results.some(sale => sale.horse.dam === null) ||
            results.some(sale => sale.consignor === null);

        const deactivateSwapHipNumberButton =
            results.length === 0 || results.some(sale => sale.hipNumber === null) || this.state.selectedHorsesForSale.length < 2;

        const selectedConsignorId = this.selectedConsignorId;
        const notSelectRow = selectedConsignorId
            ? // If one is selected, then everything except H4Ss from that consignor cannot be selected
              results.filter(hfs => hfs.consignor!.id !== selectedConsignorId).map(hfs => hfs.id)
            : // Otherwise, if we don't have hip #s assigned, then everyone is not selectable
            // In all other cases, everyone is selectable
            results.some(sale => sale.hipNumber === null)
            ? results.map(sale => sale.id)
            : [];

        const columns = [
            {
                dataField: "hipNumber",
                text: "Hip #",
                sort: true,
                formatter: (cellContent: SimpleHorseForSale | undefined, row: SimpleHorseForSale) => {
                    if (!cellContent) {
                        return "-";
                    }
                    return row.hipNumber;
                }
            },
            {
                dataField: "horse",
                text: "Horse Name",
                formatter: (cellContent: string, row: SimpleHorseForSale) => <HorseLink horse={row.horse} targetBlank={true} />
            },
            {
                dataField: "consignor",
                text: "Consignor",
                sort: true,
                formatter: (cellContent: SimpleCustomer | undefined, row: SimpleCustomer) => {
                    if (!cellContent) {
                        return "-";
                    }
                    return <CustomerLink customer={cellContent} targetBlank={true} />;
                }
            },
            {
                dataField: "dam",
                text: "Dam",
                formatter: (cellContent: string, row: SimpleHorseForSale) =>
                    row.horse.dam ? <HorseLink horse={row.horse.dam} targetBlank={true} /> : "-"
            },
            {
                dataField: "damHip#",
                text: "Dam's Hip # Generation Name",
                formatter: (cellContent: string, row: SimpleHorseForSale) => (row.horse.dam ? row.horse.dam.hipNumberName : "-")
            },
            {
                dataField: "sale",
                text: "Day of Sale",
                formatter: (cellContent: string, row: SimpleHorseForSale) => {
                    return currentSaleDates.length > 0 && row.hipNumber === null ? (
                        <div className="btn-group btn-group-toggle" data-toggle="buttons" style={{ width: "340px" }}>
                            <label
                                className={`btn btn-sm
                            ${(row.saleDate && row.saleDate.date) === null ? " btn-info " : "btn-outline-info"}`}
                            >
                                <input
                                    type="radio"
                                    name="row"
                                    onChange={this.handleOnSelectSaleDate(row, null)}
                                    checked={(row.saleDate && row.saleDate.date) === null}
                                />
                                Unassigned
                            </label>
                            {currentSaleDates.map((date: SaleDate) => (
                                <label
                                    key={date.date}
                                    className={`btn btn-sm
                                ${(row.saleDate && row.saleDate.date) === (date && date.date) ? " btn-info " : "btn-outline-info"}`}
                                >
                                    <input
                                        type="radio"
                                        name="row"
                                        value={date.date}
                                        onChange={this.handleOnSelectSaleDate(row, date)}
                                        checked={(row.saleDate && row.saleDate.date) === (date && date.date) || false}
                                    />
                                    {moment(date.date).format("MMMM DD, YYYY")}
                                </label>
                            ))}
                        </div>
                    ) : (
                        row.saleDate && moment(row.saleDate.date).format("MMM DD, YYYY")
                    );
                }
            }
        ];

        return (
            <div>
                <nav aria-label="breadcrumb">
                    <ol className="breadcrumb">
                        <li className="breadcrumb-item active" aria-current="page">
                            Assign Day of the Sale &amp; Generate Hip #
                        </li>
                    </ol>
                </nav>
                <div className="content">
                    <div className="row">
                        <div className="col-12">
                            <h1>Assign Day of the Sale & Generate Hip #</h1>
                            <div className="alert alert-info mb-3 shadow" role="alert">
                                <strong>Steps to Generate Hip #s:</strong>
                                <ol className="mb-1">
                                    <li>Select a Sale</li>
                                    <li>Assign a Day of Sale to the Horses for Sale</li>
                                    <li>Generate Hip #s</li>
                                </ol>
                                <strong>Optional:</strong>
                                <ul className="mb-1">
                                    <li>
                                        Swap Hip #s of a specific Consignor: Select the Horses for Sale and click the "Swap Hip #s of the Selected
                                        Horses" button
                                    </li>
                                    <li>Go directly to the Hip #s and Consignors Report</li>
                                </ul>
                            </div>
                            <div className="card shadow">
                                <div className="card-body">
                                    <SelectGroup
                                        label="Select Sale:"
                                        name="sale"
                                        isRequired={true}
                                        className={`col-md-12 mx-0 px-0`}
                                        disabled={false}
                                        value={this.state.selectedSale ? "" + this.state.selectedSale.id : ""}
                                        onChange={e => this.onChangeSelectSale(e)}
                                    >
                                        <option value="">Select sale...</option>
                                        {configurationStore!.horseSales
                                            .sort((a: HorseSale, b: HorseSale) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
                                            .map((c: HorseSale) => (
                                                <option key={c.id} value={c.id}>
                                                    {c.name}
                                                </option>
                                            ))}
                                    </SelectGroup>
                                    <div className="card">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-12">
                                                    <label htmlFor="consignor">Consignor</label>
                                                    <CustomerMultiselect
                                                        customerEndpoints={this.props.customerEndpoints!}
                                                        include="BOTH"
                                                        involvementSaleFilters={horseForSaleListFilters.selectedSales.map(s => s.id)}
                                                        involvementRoleFilters={["consignor"]}
                                                        value={horseForSaleListFilters.selectedConsignors}
                                                        onChange={this.onChangeSelectConsignor}
                                                        showCreate={false}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className=" alert alert-warning my-3 mx-2 text-center" style={{ fontSize: ".8rem" }} role="alert">
                                        <strong> Tip: </strong>
                                        Note that only <em>Approved</em> Horses for Sale are listed below. To be able to generate the Hip #s, every
                                        Horse for Sale needs to have a Consignor, a Dam and a Day of Sale selected.
                                        <br />
                                        <strong> Note: </strong> Horses withdrawn after Hip #s have been generated are highlighted in red.
                                    </div>
                                    <div className="row">
                                        <div className="col-12">
                                            <button
                                                type="text"
                                                className={`btn mr-4 ${deactivateGenerationHipNumbersButton ? `btn-secondary` : `btn-primary`}`}
                                                disabled={deactivateGenerationHipNumbersButton}
                                                onClick={this.generateHipNumbers(this.state.selectedSale)}
                                            >
                                                Generate Hip #s
                                            </button>
                                            <button
                                                type="text"
                                                className={`btn ${!deactivateSwapHipNumberButton ? "btn-primary" : "btn-secondary"} mr-4`}
                                                disabled={deactivateSwapHipNumberButton}
                                                onClick={() => this.setState({ showDialog: true })}
                                            >
                                                Swap Hip #s of the Selected Horses
                                                <span className="badge badge-secondary ml-2">{this.state.selectedHorsesForSale.length}</span>
                                            </button>
                                            <Link
                                                to={{
                                                    pathname: "/reports/hip-nums-and-consignors",
                                                    state: {
                                                        consignors: horseForSaleListFilters.selectedConsignors
                                                    }
                                                }}
                                            >
                                                Go to Hip #s and Consignors Report
                                            </Link>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <small>
                                            &nbsp;
                                            {infoTableArray.map((info, index, array) => {
                                                return (
                                                    <span key={info.date || 0}>
                                                        {info.date === null ? "Unassigned" : moment(info.date).format("MMMM DD, YYYY")}
                                                        <strong> ({info.length}) </strong>
                                                        {array.length === index + 1 ? "" : " - "}
                                                    </span>
                                                );
                                            })}
                                        </small>
                                    </div>
                                    <div className="table-responsive">
                                        <BootstrapTable
                                            keyField="id"
                                            remote={true}
                                            hover={true}
                                            bordered={false}
                                            columns={columns}
                                            bootstrap4={true}
                                            data={results}
                                            noDataIndication={() => (
                                                <div>{horseForSaleListStore.loading ? <LoadingWidget /> : <span>No results found.</span>}</div>
                                            )}
                                            selectRow={{
                                                mode: "checkbox",
                                                clickToSelect: false,
                                                selected: this.state.selectedHorsesForSale.map(h => h.id),
                                                onSelect: this.handleOnSelect,
                                                hideSelectAll: true,
                                                nonSelectable: notSelectRow
                                            }}
                                            onTableChange={this.tableChanged}
                                            rowClasses={(row: HorseForSale, rowIndex: number): string => {
                                                let classes = "";
                                                if (PostApprovalWithdrawnStates().indexOf(row.state) >= 0) {
                                                    classes += "row-warning";
                                                }
                                                return classes;
                                            }}
                                        />
                                        {/* This is here as a hack to make the component observe `customerListStore.loading`
                                                    Otherwise the `noDataIndication` wouldn't work */}
                                        {horseForSaleListStore.loading && <div />}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <SwapHipNumbersDialog
                    showDialog={this.state.showDialog}
                    horsesForSale={this.state.selectedHorsesForSale}
                    onClose={() => {
                        this.clearSelectHipNumbers();
                        this.setState({ showDialog: false });
                    }}
                    onComplete={() => this.props.horseForSaleListStore!.refresh()}
                />
            </div>
        );
    }

    private get selectedConsignorId(): number | null {
        const firstConsignor = this.state.selectedHorsesForSale.length > 0 ? this.state.selectedHorsesForSale[0].consignor : null;
        return (firstConsignor && firstConsignor.id) || null;
    }

    private get sortOrder(): Partial<HorseForSaleListState> {
        const results = this.props.horseForSaleListStore!.horseForSaleList.results;
        if (!results.some(sale => sale.hipNumber === null)) {
            return {
                sortField: "hipNumber",
                sortOrder: "asc"
            };
        } else {
            return {
                sortField: "horseName",
                sortOrder: "asc"
            };
        }
    }
}



// WEBPACK FOOTER //
// ./src/components/horses-for-sale/AssignDayForSaleAndGenerateHipNumbers.tsx